import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import axios from "axios";

const defaultDraft = {
  id: null,
  label: "",
  companyName: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  stateProvince: "",
  postalCode: "",
  country: "",
  isDefault: false,
};

export default function CompanyContactInfoTab({ instanceUniqueId }) {
  const [addresses, setAddresses] = useState([]);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(defaultDraft);
  const [instanceId, setInstanceId] = useState(null);

  const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
  const headers = { headers: { accessToken: localStorage.getItem("accessToken") } };

  const fetchInstanceId = async () => {
    if (!instanceUniqueId) return;
    const res = await axios.get(
      `${SERVER}/instances/byinstanceUniqueId/${instanceUniqueId}`,
      headers
    );
    setInstanceId(res.data?.id || null);
  };

  const fetchAddresses = async (targetInstanceId = instanceId) => {
    if (!targetInstanceId) return;
    const res = await axios.get(
      `${SERVER}/instances/${targetInstanceId}/company-addresses`,
      headers
    );
    setAddresses(res.data.addresses || []);
  };

  useEffect(() => {
    fetchInstanceId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceUniqueId]);

  useEffect(() => {
    if (instanceId) fetchAddresses(instanceId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceId]);

  const openCreate = () => {
    setDraft(defaultDraft);
    setOpen(true);
  };

  const openEdit = (address) => {
    setDraft({
      id: address.id,
      label: address.label || "",
      companyName: address.companyName || "",
      addressLine1: address.addressLine1 || "",
      addressLine2: address.addressLine2 || "",
      city: address.city || "",
      stateProvince: address.stateProvince || "",
      postalCode: address.postalCode || "",
      country: address.country || "",
      isDefault: !!address.isDefault,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!instanceId) return;
    const payload = {
      label: draft.label.trim(),
      companyName: draft.companyName.trim() || null,
      addressLine1: draft.addressLine1.trim(),
      addressLine2: draft.addressLine2.trim() || null,
      city: draft.city.trim() || null,
      stateProvince: draft.stateProvince.trim() || null,
      postalCode: draft.postalCode.trim() || null,
      country: draft.country.trim() || null,
      isDefault: !!draft.isDefault,
    };
    if (!payload.label || !payload.addressLine1) return;

    if (draft.id) {
      await axios.put(
        `${SERVER}/instances/${instanceId}/company-addresses/${draft.id}`,
        payload,
        headers
      );
    } else {
      await axios.post(
        `${SERVER}/instances/${instanceId}/company-addresses`,
        payload,
        headers
      );
    }
    setOpen(false);
    fetchAddresses(instanceId);
  };

  const remove = async (address) => {
    if (!instanceId) return;
    await axios.delete(
      `${SERVER}/instances/${instanceId}/company-addresses/${address.id}`,
      headers
    );
    fetchAddresses(instanceId);
  };

  const canSave = useMemo(
    () => draft.label.trim().length > 0 && draft.addressLine1.trim().length > 0,
    [draft]
  );

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Company Contact Information
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Manage company addresses used in document footers.
          </Typography>
        </Box>

        <Button variant="contained" onClick={openCreate}>
          Add Address
        </Button>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Label</TableCell>
            <TableCell>Address</TableCell>
            <TableCell>Default</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(addresses || []).map((address) => (
            <TableRow key={address.id} hover>
              <TableCell sx={{ fontWeight: 700 }}>{address.label}</TableCell>
              <TableCell>
                {[address.addressLine1, address.city, address.stateProvince]
                  .filter(Boolean)
                  .join(", ") || "—"}
              </TableCell>
              <TableCell>{address.isDefault ? "Yes" : "No"}</TableCell>
              <TableCell align="right">
                <IconButton onClick={() => openEdit(address)} size="small">
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton onClick={() => remove(address)} size="small">
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {(addresses || []).length === 0 && (
            <TableRow>
              <TableCell colSpan={4} sx={{ py: 4, textAlign: "center", opacity: 0.7 }}>
                No addresses yet. Add your first one.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {draft.id ? "Edit Address" : "Add Address"}
        </DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: 1 }}>
          <TextField
            label="Label"
            value={draft.label}
            onChange={(e) => setDraft((prev) => ({ ...prev, label: e.target.value }))}
          />
          <TextField
            label="Company Name"
            value={draft.companyName}
            onChange={(e) => setDraft((prev) => ({ ...prev, companyName: e.target.value }))}
          />
          <TextField
            label="Address Line 1"
            value={draft.addressLine1}
            onChange={(e) => setDraft((prev) => ({ ...prev, addressLine1: e.target.value }))}
          />
          <TextField
            label="Address Line 2"
            value={draft.addressLine2}
            onChange={(e) => setDraft((prev) => ({ ...prev, addressLine2: e.target.value }))}
          />
          <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: "1fr 1fr" }}>
            <TextField
              label="City"
              value={draft.city}
              onChange={(e) => setDraft((prev) => ({ ...prev, city: e.target.value }))}
            />
            <TextField
              label="State / Province"
              value={draft.stateProvince}
              onChange={(e) => setDraft((prev) => ({ ...prev, stateProvince: e.target.value }))}
            />
            <TextField
              label="Postal Code / Zip"
              value={draft.postalCode}
              onChange={(e) => setDraft((prev) => ({ ...prev, postalCode: e.target.value }))}
            />
            <TextField
              label="Country"
              value={draft.country}
              onChange={(e) => setDraft((prev) => ({ ...prev, country: e.target.value }))}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Switch
              checked={draft.isDefault}
              onChange={(e) => setDraft((prev) => ({ ...prev, isDefault: e.target.checked }))}
            />
            <Typography sx={{ fontWeight: 700 }}>Default address</Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={!canSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
